<template>
  <v-container>
    <v-card class="pa-4">
      <v-card-title class="text-h5">
        <v-icon left color="warning">mdi-calendar-sync</v-icon>
        Reparar fechas de ejercicios
      </v-card-title>
      <v-card-subtitle>
        Esta herramienta sincronizará las fechas de actualización de los ejercicios con la entrada más reciente.
      </v-card-subtitle>
      <v-divider class="my-4" />

      <v-card-text>
        <v-alert v-if="!selectedUsers.length && !processing" type="info" text>
          Seleccione los usuarios cuyos ejercicios desea reparar
        </v-alert>

        <v-checkbox
          v-model="selectAll"
          label="Seleccionar todos los usuarios"
          :disabled="processing"
          @change="toggleAllUsers"
          class="mb-2"
        ></v-checkbox>

        <v-autocomplete
          v-model="selectedUsers"
          :items="users"
          item-text="email"
          item-value="id"
          label="Seleccionar usuarios"
          multiple
          chips
          :disabled="processing"
          outlined
          prepend-inner-icon="mdi-account"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip v-if="index < 3">
              <span>{{ item.email }}</span>
            </v-chip>
            <span v-if="index === 3" class="grey--text text-caption">
              (+ {{ selectedUsers.length - 3 }} más)
            </span>
          </template>
        </v-autocomplete>

        <v-expansion-panels class="mt-4">
          <v-expansion-panel>
            <v-expansion-panel-header>
              <v-row no-gutters align="center">
                <v-icon color="primary" class="mr-2">mdi-tune</v-icon>
                Opciones de rendimiento
              </v-row>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <v-row>
                <v-col cols="12" sm="6">
                  <v-checkbox
                    v-model="fastMode"
                    label="Modo ultra rápido (sin contar ejercicios)"
                    :disabled="processing"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-checkbox
                    v-model="batchEnabled"
                    label="Procesar en lotes (más rápido)"
                    :disabled="processing"
                  ></v-checkbox>
                </v-col>
                <v-col cols="12">
                  <v-slider
                    v-model="maxConcurrentUsers"
                    label="Usuarios simultáneos"
                    hint="Mayor número = más rápido"
                    persistent-hint
                    min="1"
                    max="300"
                    thumb-label="always"
                    :disabled="processing"
                  ></v-slider>
                </v-col>
                <v-col cols="12">
                  <v-btn 
                    color="warning" 
                    @click="processAllAtOnce" 
                    :disabled="processing || selectedUsers.length === 0"
                    block
                    class="mt-2"
                  >
                    <v-icon left>mdi-flash</v-icon>
                    PROCESAR TODO DE GOLPE (Máxima velocidad)
                  </v-btn>
                </v-col>
              </v-row>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-btn 
          block 
          color="primary" 
          :disabled="selectedUsers.length === 0 || processing" 
          @click="startProcess" 
          class="mt-4"
        >
          <v-icon left>mdi-sync</v-icon>
          Reparar {{ selectedUsers.length }} usuario{{ selectedUsers.length !== 1 ? 's' : '' }}
        </v-btn>

        <v-progress-linear 
          v-if="processing" 
          :value="progress" 
          height="25" 
          striped 
          class="mt-6"
        >
          <template v-slot:default>
            <strong>{{ Math.ceil(progress) }}%</strong>
          </template>
        </v-progress-linear>

        <v-alert v-if="logs.length" type="info" text outlined class="mt-4">
          <div class="text-subtitle-1 mb-2">Resultados:</div>
          <v-list dense class="log-list">
            <v-list-item v-for="(log, i) in logs" :key="i" class="px-0 py-1">
              <v-list-item-icon v-if="log.type === 'success'" class="mr-2">
                <v-icon small color="success">mdi-check-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-icon v-else-if="log.type === 'error'" class="mr-2">
                <v-icon small color="error">mdi-alert-circle</v-icon>
              </v-list-item-icon>
              <v-list-item-icon v-else class="mr-2">
                <v-icon small color="info">mdi-information</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="text-body-2">{{ log.message }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </v-alert>
      </v-card-text>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn text color="grey" @click="reset" :disabled="processing">
          <v-icon left>mdi-refresh</v-icon>
          Reiniciar
        </v-btn>
        <v-btn 
          color="success" 
          :disabled="!completed"
          @click="$router.push('/admin')"
        >
          <v-icon left>mdi-check</v-icon>
          Finalizar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import {
  getFirestore,
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  limit,
  where,
  writeBatch,
} from "firebase/firestore";

const db = getFirestore();
const MAX_BATCH_SIZE = 500; // Firestore batch limit is 500 operations

export default {
  name: "FixExerciseDates",
  data() {
    return {
      users: [],
      selectedUsers: [],
      processing: false,
      progress: 0,
      logs: [],
      completed: false,
      totalExercises: 0,
      processedExercises: 0,
      selectAll: false,
      maxConcurrentUsers: 300, // Process 300 users at a time
      batchEnabled: true,
      fastMode: true // New option for maximum speed
    };
  },
  async mounted() {
    await this.loadUsers();
  },
  methods: {
    async loadUsers() {
      try {
        const usersRef = collection(db, "users");
        const usersSnap = await getDocs(usersRef);
        
        if (!usersSnap.empty) {
          this.users = usersSnap.docs.map(doc => ({
            
            email:  doc.id,
            ...doc.data(),
            id: doc.id
          }));
        }
        
        // Add log entry
        this.addLog(`Cargados ${this.users.length} usuarios.`);
      } catch (error) {
        console.error("Error loading users:", error);
        this.addLog(`Error al cargar usuarios: ${error.message}`, "error");
      }
    },
    
    addLog(message, type = "info") {
      this.logs.unshift({
        message,
        type,
        timestamp: new Date()
      });
    },
    
    async startProcess() {
      if (this.selectedUsers.length === 0) {
        return;
      }
      
      this.processing = true;
      this.progress = 0;
      this.completed = false;
      this.totalExercises = 0;
      this.processedExercises = 0;
      
      try {
        this.addLog(`Iniciando procesamiento para ${this.selectedUsers.length} usuarios.`);
        
        // In fast mode, skip counting exercises and just estimate
        if (this.fastMode) {
          // Estimate 10 exercises per user for progress bar (just a visual estimate)
          this.totalExercises = this.selectedUsers.length * 10;
          this.addLog("Modo rápido: omitiendo conteo de ejercicios");
          
          // Process all users at once (limited by maxConcurrentUsers)
          const chunks = this.chunkArray(this.selectedUsers, this.maxConcurrentUsers);
          let processedUsers = 0;
          
          for (const chunk of chunks) {
            // Process each chunk of users in parallel
            await Promise.all(chunk.map(userId => this.processUserExercises(userId)));
            
            // Update progress based on users processed
            processedUsers += chunk.length;
            this.progress = (processedUsers / this.selectedUsers.length) * 100;
          }
        } else {
          // Original mode with counting
          await this.calculateTotalExercises();
          
          // Process users in parallel with a limit
          const chunks = this.chunkArray(this.selectedUsers, this.maxConcurrentUsers);
          
          for (const chunk of chunks) {
            // Process each chunk of users in parallel
            await Promise.all(chunk.map(userId => this.processUserExercises(userId)));
          }
        }
        
        this.completed = true;
        this.addLog(`Proceso completado. Se actualizaron ${this.processedExercises} ejercicios en total.`, "success");
      } catch (error) {
        console.error("Error in fix process:", error);
        this.addLog(`Error en el proceso: ${error.message}`, "error");
      } finally {
        this.processing = false;
        this.progress = 100;
      }
    },
    
    // Helper function to chunk array for parallel processing
    chunkArray(array, chunkSize) {
      const chunks = [];
      for (let i = 0; i < array.length; i += chunkSize) {
        chunks.push(array.slice(i, i + chunkSize));
      }
      return chunks;
    },
    
    async calculateTotalExercises() {
      this.addLog("Calculando número total de ejercicios...");
      let total = 0;
      
      for (const userId of this.selectedUsers) {
        try {
          // Skip empty or invalid user IDs
          if (!userId || userId === '') {
            this.addLog(`Omitiendo usuario con ID inválido ${userId}.`, "error");
            continue;
          }
          
          const exercisesRef = collection(db, `users/${userId}/exercises`);
          const exercisesSnap = await getDocs(exercisesRef);
          total += exercisesSnap.size;
        } catch (error) {
          console.error(`Error counting exercises for user ${userId}:`, error);
          this.addLog(`Error al contar ejercicios para usuario ${userId}: ${error.message}`, "error");
        }
      }
      
      this.totalExercises = total;
      this.addLog(`Total de ejercicios a procesar: ${total}`);
    },
    
    async processUserExercises(userId) {
      // Skip empty or invalid user IDs
      if (!userId || userId === '') {
        this.addLog(`Omitiendo usuario con ID inválido ${userId}.`, "error");
        return;
      }
      
      const userEmail = this.users.find(u => u.id === userId)?.email || userId;
      
      try {
        // Fast mode reduces logging and focuses on speed
        if (!this.fastMode) {
          this.addLog(`Procesando usuario: ${userEmail}`);
        }
        
        // Get all exercises for this user
        const exercisesRef = collection(db, `users/${userId}/exercises`);
        const exercisesSnap = await getDocs(exercisesRef);
        
        if (exercisesSnap.empty) {
          if (!this.fastMode) {
            this.addLog(`No se encontraron ejercicios para ${userEmail}.`);
          }
          return;
        }
        
        const exercises = exercisesSnap.docs;
        
        if (this.batchEnabled) {
          // Process exercises in batches for better performance
          await this.processExercisesInBatches(userId, exercises, userEmail);
        } else {
          // Process each exercise individually (slower)
          for (const exercise of exercises) {
            const exerciseId = exercise.id;
            const exerciseName = exercise.data().name || 'Desconocido';
            
            await this.updateExerciseDate(userId, exerciseId, exerciseName);
            
            this.processedExercises++;
            
            // In fast mode, we only update progress based on users, not exercises
            if (!this.fastMode) {
              this.updateProgress();
            }
          }
        }
        
        if (!this.fastMode) {
          this.addLog(`Completado usuario ${userEmail}. ${exercises.length} ejercicios procesados.`, "success");
        }
      } catch (error) {
        console.error(`Error processing user ${userId}:`, error);
        this.addLog(`Error al procesar usuario ${userEmail}: ${error.message}`, "error");
      }
    },
    
    async processExercisesInBatches(userId, exercises, userEmail) {
      // Process exercises in batches to reduce Firebase operations
      const batches = this.chunkArray(exercises, MAX_BATCH_SIZE);
      
      for (const batch of batches) {
        try {
          await this.processBatch(userId, batch);
          this.processedExercises += batch.length;
          
          // In fast mode, we only update progress based on users, not exercises
          if (!this.fastMode) {
            this.updateProgress();
          }
        } catch (error) {
          console.error(`Error processing batch for user ${userEmail}:`, error);
          if (!this.fastMode) {
            this.addLog(`Error al procesar lote para ${userEmail}: ${error.message}`, "error");
          }
        }
      }
    },
    
    async processBatch(userId, exercises) {
      // First collect all the data needed for updates
      const updates = [];
      
      for (const exercise of exercises) {
        const exerciseId = exercise.id;
        
        // Skip empty or invalid exercise IDs
        if (!exerciseId || exerciseId === '') {
          continue;
        }
        
        const exerciseName = exercise.data().name || 'Desconocido';
        
        try {
          // Get the most recent entry date
          const entriesRef = collection(db, `users/${userId}/exercises/${exerciseId}/entries`);
          const entriesQuery = query(entriesRef, orderBy("date", "desc"), limit(1));
          const entriesSnap = await getDocs(entriesQuery);
          
          if (!entriesSnap.empty) {
            const mostRecentDate = entriesSnap.docs[0].data().date;
            updates.push({
              exerciseId,
              exerciseName,
              mostRecentDate
            });
          }
        } catch (error) {
          if (!this.fastMode) {
            console.error(`Error getting entries for exercise ${exerciseId}:`, error);
          }
        }
      }
      
      // Now perform batch update
      if (updates.length > 0) {
        const batch = writeBatch(db);
        
        for (const update of updates) {
          const exerciseRef = doc(db, `users/${userId}/exercises/${update.exerciseId}`);
          batch.update(exerciseRef, { lastUpdateDate: update.mostRecentDate });
        }
        
        await batch.commit();
        if (!this.fastMode) {
          this.addLog(`Actualizados ${updates.length} ejercicios en lote para un usuario.`, "success");
        }
      }
    },
    
    async updateExerciseDate(userId, exerciseId, exerciseName) {
      // Skip empty or invalid exercise IDs
      if (!exerciseId || exerciseId === '') {
        return;
      }
      
      try {
        // Get entries for this exercise, ordered by date descending (most recent first)
        const entriesRef = collection(db, `users/${userId}/exercises/${exerciseId}/entries`);
        const entriesQuery = query(entriesRef, orderBy("date", "desc"), limit(1));
        const entriesSnap = await getDocs(entriesQuery);
        
        if (entriesSnap.empty) {
          return;
        }
        
        // Get most recent entry
        const mostRecentEntry = entriesSnap.docs[0];
        const mostRecentDate = mostRecentEntry.data().date;
        
        // Update the exercise document with this date
        const exerciseRef = doc(db, `users/${userId}/exercises/${exerciseId}`);
        await updateDoc(exerciseRef, {
          lastUpdateDate: mostRecentDate
        });
      } catch (error) {
        console.error(`Error updating exercise ${exerciseId}:`, error);
        this.addLog(`Error al actualizar ejercicio ${exerciseName}: ${error.message}`, "error");
      }
    },
    
    updateProgress() {
      // Update progress percentage
      this.progress = (this.processedExercises / this.totalExercises) * 100;
    },
    
    reset() {
      this.selectedUsers = [];
      this.logs = [];
      this.completed = false;
      this.progress = 0;
      this.totalExercises = 0;
      this.processedExercises = 0;
      this.selectAll = false;
      // We don't reset performance settings as these are user preferences
    },

    toggleAllUsers() {
      if (this.selectAll) {
        this.selectedUsers = this.users.map(user => user.id);
      } else {
        this.selectedUsers = [];
      }
    },

    async processAllAtOnce() {
      if (this.selectedUsers.length === 0) {
        return;
      }
      
      this.processing = true;
      this.progress = 0;
      this.completed = false;
      this.totalExercises = 0;
      this.processedExercises = 0;
      
      try {
        this.addLog(`Procesando TODOS los usuarios (${this.selectedUsers.length}) simultáneamente.`);
        
        // Use artificial progress based on time
        const startTime = Date.now();
        const expectedDuration = 60000; // 60 seconds estimate
        
        // Start progress timer
        const progressTimer = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progressPct = Math.min(95, (elapsed / expectedDuration) * 100);
          this.progress = progressPct;
        }, 500);
        
        // Process ALL users simultaneously without chunking
        await Promise.all(this.selectedUsers.map(userId => this.processUserFast(userId)));
        
        // Clear the progress timer
        clearInterval(progressTimer);
        
        this.completed = true;
        this.addLog(`Proceso completado en modo máxima velocidad.`, "success");
      } catch (error) {
        console.error("Error in ultra-fast process:", error);
        this.addLog(`Error en el proceso: ${error.message}`, "error");
      } finally {
        this.processing = false;
        this.progress = 100;
      }
    },
    
    async processUserFast(userId) {
      // Skip invalid IDs
      if (!userId || userId === '') {
        return;
      }
      
      try {
        // Get all exercises in a single query
        const exercisesRef = collection(db, `users/${userId}/exercises`);
        const exercisesSnap = await getDocs(exercisesRef);
        
        if (exercisesSnap.empty) {
          return;
        }
        
        // Create an array of all necessary queries for entries
        const entryQueries = exercisesSnap.docs.map(exercise => {
          const exerciseId = exercise.id;
          if (!exerciseId) return null;
          
          const entriesRef = collection(db, `users/${userId}/exercises/${exerciseId}/entries`);
          const entriesQuery = query(entriesRef, orderBy("date", "desc"), limit(1));
          return { 
            exerciseId,
            query: entriesQuery
          };
        }).filter(q => q !== null);
        
        // Execute all entry queries in parallel
        const entryResults = await Promise.all(
          entryQueries.map(async (q) => {
            try {
              const entriesSnap = await getDocs(q.query);
              if (!entriesSnap.empty) {
                return {
                  exerciseId: q.exerciseId,
                  mostRecentDate: entriesSnap.docs[0].data().date
                };
              }
              return null;
            } catch (error) {
              return null;
            }
          })
        );
        
        // Filter out nulls
        const updates = entryResults.filter(result => result !== null);
        
        if (updates.length === 0) {
          return;
        }
        
        // Update in batches (Firestore limit is 500 operations per batch)
        const batchSize = 500;
        for (let i = 0; i < updates.length; i += batchSize) {
          const batch = writeBatch(db);
          const chunk = updates.slice(i, i + batchSize);
          
          for (const update of chunk) {
            const exerciseRef = doc(db, `users/${userId}/exercises/${update.exerciseId}`);
            batch.update(exerciseRef, { lastUpdateDate: update.mostRecentDate });
          }
          
          await batch.commit();
          this.processedExercises += chunk.length;
        }
      } catch (error) {
        console.error(`Error in fast mode for user ${userId}:`, error);
      }
    },
  },
  watch: {
    selectedUsers(newValue) {
      // Update selectAll based on whether all users are selected
      this.selectAll = newValue.length > 0 && newValue.length === this.users.length;
    }
  }
};
</script>

<style scoped>
.log-list {
  max-height: 300px;
  overflow-y: auto;
  background-color: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  padding: 8px;
}
</style> 